import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Command, HistoryEntry, RestorePoint } from '@integrate/contracts';
import { historyRows, type HistoryNote, type HistoryRow } from '@integrate/core';
import { api, ApiError } from '../api.js';
import { Modal } from '../ui/Modal.js';
import { Loading } from '../ui/States.js';

/*
  The change log, and the first undo the platform has ever offered a user.

  A MODAL, not the Activity ticker, and the ticker stays what it is. Three structural reasons, none of
  them about layout. The ticker's rows come off the socket, and a socket frame carries no `undone` and no
  `blocked` — those are computed per read, out of columns no frame has. The ticker is empty on page load,
  which is the one moment somebody most wants to undo something. And `undone` changes RETROACTIVELY on
  rows already delivered: reverting a change marks it undone, and reverting that revert clears it again,
  while the socket never re-sends an old event. A list built from frames can never render that flag right.

  So the log is fetched, and refetched after every revert — a revert changes the flags of rows it did not
  create, and nothing broadcasts that.

  WHAT A ROW OFFERS. One row per batch, with one button naming its own scope, because reverting any event
  of a batch reverts all of them. A row that cannot be undone shows the reason instead of a disabled
  control: "this can never be undone", "somebody already took it back" and "there is nothing here to undo"
  are three different things to tell somebody, and a greyed-out button says none of them.

  NO FILTERS, AND NO PAGING, DELIBERATELY — and written down here because it is the one divergence from
  the frozen app's change log that nothing else records. That log offers type chips, a date range, a
  field filter, a scenario filter, a perspective filter and free-text search (orgchart.html:8631,
  8647-8656). This has newest-first and a per-row undo.

  The reason is that those are two different jobs. What this surface is for is "what just happened, and
  can I take it back", which newest-first answers directly and a filter bar mostly gets in the way of.
  The other job — "find the decision about the Warsaw team from March" — wants searching on the SERVER
  rather than filtering a page the server already truncated. Building the filters first would produce a
  control that searches 500 rows and quietly finds nothing in the older ones.

  So the order, when it comes: a cursor on /history and a "load more" here, then filtering — server-side,
  over the whole log. Until the first project passes 500 changes neither is urgent, and the cap discards
  the oldest rather than the newest, which is the right way round.

  WHY, WHICH THE LOG OTHERWISE NEVER RECORDS. Two note paths, and they are the frozen app's two, minus its
  interruption.

  A free-standing note is the workshop decision a run of changes came out of — the app's "＋ Add note"
  (orgchart.html:8680), here `addNote`, a change with no writes whose event IS the record. A note against
  a row is the "why" for one change — the app's post-change prompt, here `annotateEvent`, which had
  written a projection since P3 that nothing read back.

  WHAT IS DELIBERATELY NOT PORTED is the prompt itself: a modal that opens after EVERY logged change
  (`promptNote`, `:2184`), with a session snooze and a disable toggle to escape it. Two reasons, and the
  first is not "it is annoying". This client applies optimistically against a live socket, so changes land
  from colleagues as well as from the reader; a modal keyed to "a change happened" would open on somebody
  else's work, over the reader's own half-finished edit. And a prompt whose commonest answer is to dismiss
  it trains the dismissal — the app shipped two separate escapes from its own feature, which is the
  measure of how often it was unwanted. So the capability is here and the interruption is not: annotate
  any change, at any time, from the row it is about. `state.logPrompt` has no equivalent and does not
  become a preference.

  AND THE TWO REFUSALS THAT ARRIVE LATE. Whether somebody has built on this change, and whether it is
  still the newest change (which is what an undo running a destructive inverse requires), are both
  questions about every event after it, and both answers move while the modal is open. The server gives
  them at click time, and each lands at the row that was clicked — the global banner is behind this modal,
  and the message names a specific other change.
*/

/** Local time, day and minute. A change log is read in the reader's own timezone. */
function when(iso: string): string {
  const at = new Date(iso);
  return `${at.toLocaleDateString()} ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * The composer key for the free-standing note, which belongs to no row.
 *
 * Cannot collide by construction: `historyRows` keys a row on its batch id (`b_…`) or on `seq:<n>`.
 */
const LOG_KEY = 'log';

/**
 * The one textarea both note paths use.
 *
 * At module scope rather than inside `HistoryModal`, which is not a style preference: a component
 * declared inside a render body is a NEW component type on every render, so React unmounts and remounts
 * it — and a remounted textarea loses focus and the caret after every keystroke.
 */
/**
 * The points this project can be restored to, and the one control that acts on one.
 *
 * INSIDE THE HISTORY MODAL rather than on a settings screen, because this is where the offer is already
 * made: every change with no inverse prints "restore the project to a point in time instead", and until
 * now that sentence pointed at nothing. A remedy named in five messages and reachable from none of them
 * is worse than no remedy.
 *
 * COLLAPSED BY DEFAULT. The common reason to open this modal is to undo one change, and a list of
 * whole-project rollbacks sitting above it is an invitation to reach for the largest tool first.
 *
 * OWNER-ONLY, and not rendered at all for anybody else rather than rendered disabled. A control that
 * cannot be used tells a reader they are missing something; the message underneath the blocked row
 * already says what to do, which is ask an owner.
 *
 * The confirmation is the project's name typed back, as `rebuildRosterFromSource` has. Both replace
 * everybody's work at once, including work in scenarios the person acting is not looking at.
 */
function RestorePoints({
  projectId,
  projectName,
  onRestore,
}: {
  projectId: string;
  projectName: string;
  onRestore(seq: number, confirmProjectName: string): Promise<string | null>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [points, setPoints] = useState<RestorePoint[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** The point being confirmed. One at a time: this is not an action to have two of in flight. */
  const [chosen, setChosen] = useState<number | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let live = true;
    void (async () => {
      try {
        const { points: fetched } = await api.restorePoints(projectId);
        // Newest first, matching the log above it — a reader scanning both should not have to reverse
        // one of them in their head.
        if (live) setPoints([...fetched].reverse());
      } catch (err) {
        if (live) {
          setLoadError(err instanceof ApiError ? err.message : 'The restore points could not be loaded');
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [open, projectId]);

  async function confirm(seq: number): Promise<void> {
    setBusy(true);
    const message = await onRestore(seq, typed);
    setBusy(false);
    if (message === null) {
      setChosen(null);
      setTyped('');
      setRefusal(null);
      return;
    }
    setRefusal(message);
  }

  if (!open) {
    return (
      <button className="linkish" data-testid="restore-points-open" onClick={() => setOpen(true)}>
        ↩ Restore the project to a point in time
      </button>
    );
  }

  return (
    <div data-testid="restore-points">
      <p className="small muted">
        The project as it stood at each of these moments. A restore replaces every scenario’s design at
        once and is itself recorded as a change — nothing is erased, and a point is taken immediately
        before it so this can be undone by restoring again.
      </p>
      {loadError && (
        <p className="bad small" data-testid="restore-points-error">
          {loadError}
        </p>
      )}
      {points !== null && points.length === 0 && (
        <p className="small muted">This project has no restore points yet.</p>
      )}
      <ul className="plain">
        {(points ?? []).map((point) => (
          <li key={point.seq} data-testid={`restore-point-${point.seq}`}>
            <div>
              <strong>#{point.seq}</strong>{' '}
              <span className="muted small">{when(point.createdAt)}</span>
              {/*
                The change this point sits immediately BEFORE, which is the whole of what makes the list
                readable: "#38, pre-destructive" is a coordinate somebody has to go and look up.
              */}
              {point.nextSummary !== null && (
                <div className="small">before “{point.nextSummary}”</div>
              )}
              <div className="small muted">{reasonLabel(point.reason)}</div>
            </div>
            {chosen === point.seq ? (
              <div className="row">
                <input
                  data-testid="restore-confirm-name"
                  placeholder={projectName}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  aria-label={`Type ${projectName} to confirm restoring to #${point.seq}`}
                />
                <button
                  className="danger"
                  data-testid={`restore-confirm-${point.seq}`}
                  disabled={busy || typed !== projectName}
                  onClick={() => void confirm(point.seq)}
                >
                  {busy ? 'Restoring…' : 'Restore'}
                </button>
                <button
                  data-testid="restore-cancel"
                  disabled={busy}
                  onClick={() => {
                    setChosen(null);
                    setTyped('');
                    setRefusal(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                data-testid={`restore-to-${point.seq}`}
                onClick={() => {
                  setChosen(point.seq);
                  setTyped('');
                  setRefusal(null);
                }}
              >
                Restore to here
              </button>
            )}
            {refusal !== null && chosen === point.seq && (
              <p className="bad small" data-testid={`restore-error-${point.seq}`}>
                {refusal}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Why a point exists, in words.
 *
 * The stored value is a storage detail, and the difference matters to a reader choosing between two
 * points: one taken deliberately before a roster refresh is a rescue, one from the cadence is where the
 * clock happened to land. Unknown values pass through rather than being swallowed, so a reason added
 * later reaches the reader instead of being silently rendered as something else.
 */
function reasonLabel(reason: string): string {
  switch (reason) {
    case 'import':
      return 'the state it was imported with';
    case 'pre-destructive':
      return 'taken before a change that could not be undone';
    case 'cadence':
      return 'an automatic checkpoint';
    // The pack's own name arrives through `nextSummary` — the change the point sits before IS the
    // "SteerCo pack … issued" event — so this label carries the kind and that line carries the name.
    case 'steerco':
      return 'the state a SteerCo pack was rendered from';
    case 'backfill':
      return 'where the project stood when restore points began';
    default:
      return reason;
  }
}

function NoteComposer({
  testId,
  value,
  placeholder,
  hint,
  saveLabel,
  busy,
  canSave,
  onChange,
  onSave,
  onCancel,
}: {
  testId: string;
  value: string;
  placeholder: string;
  hint?: string;
  saveLabel: string;
  busy: boolean;
  canSave: boolean;
  onChange(next: string): void;
  onSave(): void;
  onCancel(): void;
}): JSX.Element {
  return (
    <div className="hcompose" data-testid={`${testId}-composer`}>
      <textarea
        data-testid={`${testId}-text`}
        rows={3}
        autoFocus
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        // Escape cancels; Enter is left alone, because these are sentences and a note worth writing
        // takes more than one line. The frozen app bound Enter to save and said so in its placeholder.
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onCancel();
          }
        }}
      />
      <div className="hcompose-act">
        {hint && <span className="muted small">{hint}</span>}
        <span className="spacer" />
        <button data-testid={`${testId}-cancel`} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button data-testid={`${testId}-save`} onClick={onSave} disabled={busy || !canSave}>
          {busy ? 'Saving…' : saveLabel}
        </button>
      </div>
    </div>
  );
}

/** A note's own byline, kept out of the row so the two authors are never confused for one. */
function NoteLine({ note, seq }: { note: HistoryNote; seq: number }): JSX.Element {
  return (
    <p className="hnote" data-testid={`history-note-${note.seq}`}>
      <span className="hnote-text">{note.text}</span>{' '}
      <span className="muted small">
        — {note.authorName || 'Someone'}, {when(note.updatedAt)}
        {/* Only when it is not the row's own headline event, so a batch says which change it is about. */}
        {note.seq !== seq && <> · on #{note.seq}</>}
      </span>
    </p>
  );
}

export function HistoryModal({
  projectId,
  scenarios,
  scenarioId,
  seq,
  myUserId,
  canWrite,
  canComment,
  isOwner,
  projectName,
  onRevert,
  onNote,
  onRestore,
  onClose,
}: {
  projectId: string;
  scenarios: readonly { id: string; name: string }[];
  /**
   * The scenario a free-standing note is filed under, so the log can say which design it was about.
   *
   * Not used for `annotateEvent`, which inherits the annotated change's own scenario — a note about a
   * decision belongs where that decision was made, not where the reader happens to be standing.
   */
  scenarioId: string | null;
  /**
   * The project's sequence number, used as a reload trigger and nothing else.
   *
   * A revert changes the flags of rows it did not create — it marks one undone, and a redo clears that
   * again — and the realtime hub publishes only the events the revert appended, never "seq 12 is now
   * undone". The socket also never re-sends an old event. So a colleague's undo would leave this list
   * showing a button on a change that has already been taken back until it was closed and reopened.
   * `seq` moves on every change from anybody, which is exactly when these flags might have.
   */
  seq: number;
  /**
   * This reader's user id, for the "only mine" filter. Null while the session is still resolving.
   *
   * The id rather than the name: two colleagues can share a display name, and a filter that quietly
   * includes somebody else's change is worse than no filter on a list with undo buttons on it.
   */
  myUserId: string | null;
  canWrite: boolean;
  /**
   * Whether this reader may put a remark on the record — WIDER than `canWrite`, not narrower.
   *
   * Its own prop for the same reason `isOwner` is its own: the three gate different things, and the one
   * that was conflated here is the one that mattered. `canWrite` hid the note composer, so a reviewer —
   * whose entire purpose is seeing a problem and saying so — opened this modal and had nowhere to say it.
   * A note writes no design state and has an empty inverse, so it is not a write in the sense `canWrite`
   * is asking about.
   */
  canComment: boolean;
  /**
   * Whether this reader owns the project, which is a stricter question than `canWrite`.
   *
   * Its own prop rather than derived from `canWrite`, because the two gate different things and
   * conflating them is how an editor ends up offered a control the server will refuse: an editor
   * designs, an owner decides what the project IS.
   */
  isOwner: boolean;
  /** The project's own name, which the restore confirmation requires typed back exactly. */
  projectName: string;
  /** Resolves to null on success, or the server's refusal for this row. */
  onRevert(seq: number): Promise<string | null>;
  /** Same contract as `onRevert`, and for the same reason: the banner is behind this modal. */
  onNote(command: Extract<Command, { type: 'addNote' | 'annotateEvent' }>): Promise<string | null>;
  /** Same contract again. Resolves to null on success, or the server's refusal. */
  onRestore(seq: number, confirmProjectName: string): Promise<string | null>;
  onClose(): void;
}): JSX.Element {
  const [events, setEvents] = useState<HistoryEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** The row a refusal belongs to, keyed by row so it cannot be shown against a different change. */
  const [refusals, setRefusals] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  /**
   * Which composer is open — a row's key, or `LOG_KEY` for the free-standing one. One at a time.
   *
   * One rather than a map because a half-written note in a collapsed row is a note somebody will lose
   * without noticing. Opening a second composer closes the first, visibly.
   */
  const [composing, setComposing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  /**
   * Show only this reader's own changes.
   *
   * Not persisted to the prefs row, deliberately. The other per-user lenses there — filters, view mode,
   * grouping — are how you have decided to look at the project and should survive a reload; this is
   * something you turn on to find one change you just made, and a log that silently opens showing a
   * fraction of the project is a log that lies by omission.
   */
  const [onlyMine, setOnlyMine] = useState(false);

  const load = useCallback(async () => {
    try {
      setEvents((await api.history(projectId)).events);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'The history could not be loaded');
    }
  }, [projectId]);

  /*
    Fetched data in state, the view of it derived. `scenarios` deliberately does not gate the fetch: it
    arrives as a fresh array on every resync, so making it a dependency of `load` would refetch the whole
    log on every change anywhere on the project, for a list of names that has not moved.
  */
  /*
    FILTERED BEFORE THE ROWS ARE BUILT, not after, and that is safe for one specific reason: a batch is
    one execution by one user, so every event under a batch id shares an actor. Filtering entries can
    therefore never split a batch and leave `historyRows` describing half of one — which is exactly what
    it would do if the filter were, say, by scenario.

    `batchSize` and `staleAfterSeq` are computed by the server over the whole project and travel on the
    entry, so they survive the filter intact: a row still knows it is one of forty, and still knows a
    later change has made it stale, even in a list that shows none of the others.
  */
  const mineOnly = onlyMine && myUserId !== null;
  const rows: HistoryRow[] | null = useMemo(() => {
    if (events === null) return null;
    const visible = mineOnly ? events.filter((e) => e.actorUserId === myUserId) : events;
    /*
      The FULL list goes through as well, because the supersede prediction has to see changes this list is
      not showing. A colleague's later change is the commonest reason an undo refuses, and with "only mine"
      ticked there are none of theirs in `visible` — scanning that would put the button back on exactly the
      rows the prediction exists to take it off.
    */
    return historyRows(visible, { scenarios, allEvents: events });
  }, [events, scenarios, mineOnly, myUserId]);

  useEffect(() => {
    void load();
    // `seq` is a dependency, not a value this reads: any change on the project may have moved a flag here.
  }, [load, seq]);

  async function undo(row: HistoryRow): Promise<void> {
    setBusy(row.key);
    const refusal = await onRevert(row.seq);
    setBusy(null);
    setRefusals((r) => {
      const { [row.key]: _cleared, ...rest } = r;
      return refusal ? { ...rest, [row.key]: refusal } : rest;
    });
    // Refetched either way: on success because the flags of other rows have moved, and on a refusal
    // because the reason usually names a change the reader now wants to look at.
    await load();
  }

  function openComposer(key: string, initial: string): void {
    setComposing(key);
    setDraft(initial);
    // The previous attempt's refusal goes with the draft it was about, or it reads as a fresh failure.
    setRefusals((r) => {
      const { [key]: _cleared, ...rest } = r;
      return rest;
    });
  }

  async function saveNote(
    key: string,
    command: Extract<Command, { type: 'addNote' | 'annotateEvent' }>,
  ): Promise<void> {
    setBusy(key);
    const refusal = await onNote(command);
    setBusy(null);
    setRefusals((r) => {
      const { [key]: _cleared, ...rest } = r;
      return refusal ? { ...rest, [key]: refusal } : rest;
    });
    // Left open on a refusal, so the text the reader typed is still there to retry or copy out of.
    if (refusal !== null) return;
    setComposing(null);
    setDraft('');
    // A note is a new event and an annotation moves a row's own note, so the list has changed either way.
    // The `seq` effect would also fire; this makes the refresh part of the action rather than of a prop.
    await load();
  }

  return (
    <Modal
      testId="history-modal"
      title="History"
      onClose={onClose}
      wide
      footer={
        <>
          <span className="spacer" />
          <button onClick={onClose}>Done</button>
        </>
      }
    >
      <p className="muted">
        Every change to this project, newest first. Undoing one records a new change rather than
        erasing it — a history you can rewrite is not an audit trail.
      </p>

      {loadError && (
        <p className="bad" data-testid="history-load-error">
          {loadError}
        </p>
      )}

      {/*
        "Only mine", and what it does NOT mean.

        It narrows what is LISTED, not what can be undone, and the difference is worth being plain
        about: both revert guards count later changes by anybody. A colleague touching an overlapping
        key still blocks your undo, and a destructive inverse is still offered only while yours is the
        newest change on the project. So this is for finding your change in a busy log — the reason
        each blocked row gives is already printed underneath it, and now names the person.
      */}
      {myUserId !== null && (
        <label className="small muted" data-testid="history-only-mine-label">
          <input
            type="checkbox"
            data-testid="history-only-mine"
            checked={onlyMine}
            onChange={(e) => setOnlyMine(e.target.checked)}
          />{' '}
          Only my changes
        </label>
      )}

      {/*
        The remedy every irreversible row already points at, finally reachable from where it is
        named. Below the log's own controls rather than above them: undoing one change is the common
        case, and a whole-project rollback should not be the first thing the eye lands on.
      */}
      {isOwner && (
        <RestorePoints projectId={projectId} projectName={projectName} onRestore={onRestore} />
      )}

      {/*
        The workshop decision a run of changes came out of, filed as its own entry.

        Above the list rather than in the footer because it becomes the top row the moment it is
        saved, and a control that writes the thing directly beneath it needs no explaining.
      */}
      {canComment && composing !== LOG_KEY && (
        <button
          className="linkish"
          data-testid="history-add-note"
          onClick={() => openComposer(LOG_KEY, '')}
        >
          ＋ Note a decision
        </button>
      )}
      {canComment && composing === LOG_KEY && (
        <NoteComposer
          testId="history-log-note"
          value={draft}
          placeholder="What was decided, and why — the log records what changed, not the reason."
          saveLabel="Add to the log"
          busy={busy === LOG_KEY}
          canSave={draft.trim().length > 0}
          onChange={setDraft}
          onCancel={() => {
            setComposing(null);
            setDraft('');
          }}
          onSave={() =>
            void saveNote(LOG_KEY, {
              type: 'addNote',
              scenarioId,
              note: draft.trim(),
            })
          }
        />
      )}
      {refusals[LOG_KEY] && (
        <p className="bad small" data-testid="history-log-note-error">
          {refusals[LOG_KEY]}
        </p>
      )}

      {rows === null && !loadError && <Loading what="the history" testId="history-loading" />}

      {rows !== null && rows.length === 0 && (
        /*
          TWO DIFFERENT FACTS, and one sentence used to assert the stronger of them regardless.

          "Nothing has changed yet" is a claim about the PROJECT, and it was rendered whenever the
          list came back empty — including when a filter was the reason. The reachable case is not
          exotic: the History button is deliberately open to a read-only reviewer, and the checkbox is
          gated only on being signed in, so somebody who by role can never have made a change is
          offered "only mine" and guaranteed to be told the project has no history.
        */
        <p className="muted" data-testid="history-empty">
          {mineOnly && (events?.length ?? 0) > 0
            ? 'None of these changes are yours. Untick “Only my changes” to see the rest.'
            : 'Nothing has changed yet.'}
        </p>
      )}

      {rows !== null && rows.length > 0 && (
        <ul className="history" data-testid="history-rows" data-count={rows.length}>
          {rows.map((row) => {
            // The note the composer edits: the one on the row's own headline event. Notes on other
            // members of the batch are shown but not editable from here, because the control that
            // opened would have to say which change it was about to be honest.
            const anchorNote = row.notes.find((n) => n.seq === row.seq);
            return (
            <li
              key={row.key}
              className={row.undone ? 'hrow undone' : 'hrow'}
              data-testid={`history-row-${row.seq}`}
              data-seq={row.seq}
              data-size={row.size}
              data-undone={row.undone ? '1' : '0'}
              data-can-revert={row.canRevert ? '1' : '0'}
              data-partial={row.partial ? '1' : '0'}
            >
              <div className="hmain">
                <div className="hsummary">
                  {row.summary}
                  {row.undone && <span className="tag" data-testid={`history-undone-${row.seq}`}>Undone</span>}
                  {row.size > 1 && (
                    <span className="tag">{row.size} changes</span>
                  )}
                </div>
                <div className="when">
                  <span className="who">{row.actorName || 'Someone'}</span> · {when(row.createdAt)}
                  {row.scenarioName && <> · {row.scenarioName}</>}
                  {' · #'}
                  {row.seq}
                </div>
              </div>

              <div className="hact">
                {row.canRevert && canWrite && (
                  <button
                    data-testid={`history-undo-${row.seq}`}
                    disabled={busy === row.key}
                    onClick={() => void undo(row)}
                  >
                    {busy === row.key ? 'Undoing…' : row.undoLabel}
                  </button>
                )}
                {/*
                  A reader sees the log and no buttons. Said once at the top rather than repeated as a
                  reason on every row, which would drown the rows that have a real reason.
                */}
                {row.canRevert && !canWrite && <span className="muted small">Read-only</span>}
              </div>

              {/*
                Full width, and under the summary rather than in the action column. These sentences
                are long — the reason a roster refresh cannot be undone runs to three lines — and set
                in a narrow right-aligned column they read as a ragged block beside the thing they
                are about.
              */}
              {!row.canRevert && (
                <p className="muted small hwhy" data-testid={`history-blocked-${row.seq}`}>
                  {row.blockedReason}
                </p>
              )}

              {row.size > 1 && (
                <div className="hbatch">
                  <button
                    className="linkish"
                    data-testid={`history-expand-${row.seq}`}
                    onClick={() => setExpanded((e) => ({ ...e, [row.key]: !e[row.key] }))}
                  >
                    {expanded[row.key] ? 'Hide' : 'Show'} what this took off
                  </button>
                  {expanded[row.key] && (
                    <ul className="hmembers" data-testid={`history-members-${row.seq}`}>
                      {row.members.map((m) => (
                        <li key={m.seq} data-seq={m.seq}>
                          <span className="muted small">#{m.seq}</span> {m.summary}
                        </li>
                      ))}
                      {row.partial && (
                        <li className="muted small" data-testid={`history-partial-${row.seq}`}>
                          {row.size - row.members.length} more in this change, older than this page.
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              )}

              {/*
                The why, which nothing else in the row records. Every note on the batch, not just the
                anchor's — the API can annotate any event, and one this list dropped would be a note
                somebody wrote and nobody can find.
              */}
              {row.notes.map((note) => (
                <NoteLine key={note.seq} note={note} seq={row.seq} />
              ))}

              {canComment && composing !== row.key && (
                <button
                  className="linkish"
                  data-testid={`history-annotate-${row.seq}`}
                  onClick={() => openComposer(row.key, anchorNote?.text ?? '')}
                >
                  {anchorNote ? 'Edit this note' : 'Say why'}
                </button>
              )}
              {canComment && composing === row.key && (
                <NoteComposer
                  testId={`history-row-note-${row.seq}`}
                  value={draft}
                  placeholder="Why this change?"
                  hint={anchorNote ? 'Saving it empty removes the note.' : undefined}
                  saveLabel={anchorNote ? 'Save the note' : 'Add the note'}
                  busy={busy === row.key}
                  // An empty draft is a real instruction when a note exists — it removes it — and
                  // nothing at all when one does not.
                  canSave={draft.trim().length > 0 || anchorNote !== undefined}
                  onChange={setDraft}
                  onCancel={() => {
                    setComposing(null);
                    setDraft('');
                  }}
                  onSave={() =>
                    void saveNote(row.key, {
                      type: 'annotateEvent',
                      eventId: row.anchorEventId,
                      note: draft.trim(),
                    })
                  }
                />
              )}

              {/* One error slot per row, shared by the undo and the note: only one of them can be
                  in flight, and both are about this change. */}
              {refusals[row.key] && (
                <p className="bad small" data-testid={`history-error-${row.seq}`}>
                  {refusals[row.key]}
                </p>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
